/**
 * Intaky design tokens — all colours, sizes, and type styles live here.
 * Prefix every exported symbol with `INTAKY_` so tree-shaking and future
 * extraction to a standalone bundle stay clean.
 *
 * Design inspiration: Flighty airports — clean light surfaces, crisp
 * Inter typography, vivid status colours, glassy header.
 *
 * 4-tier colour system (customer perspective):
 *   new    (blue)  — just came in, Finch is starting work
 *   chase  (amber) — actively pursuing: calls, texts, follow-ups
 *   signed (green) — won! retained / signed
 *   closed (gray)  — done: rejected, archived, lost
 */

// ─── Status tier system ─────────────────────────────────────────────────────

export type IntakyStatusTier = 'new' | 'chase' | 'signed' | 'closed';

interface StatusTierConfig {
  tier: IntakyStatusTier;
  label: string;
  /** Gradient for map dots */
  gradient: string;
  /** Solid dot color */
  dot: string;
  /** Background tint for badges / table cells */
  bg: string;
  /** Mid-ring color for nested circles */
  mid: string;
  /** Text / icon color */
  color: string;
  /** Semi-transparent ring color for map animations */
  ring: string;
}

export const INTAKY_TIERS: Record<IntakyStatusTier, StatusTierConfig> = {
  new: {
    tier: 'new',
    label: 'New',
    gradient: 'linear-gradient(145deg, #3B82F6, #60A5FA)',
    dot: '#3B82F6',
    bg: '#DBEAFE',
    mid: '#BFDBFE',
    color: '#1D4ED8',
    ring: 'rgba(59, 130, 246, 0.15)',
  },
  chase: {
    tier: 'chase',
    label: 'In Chase',
    gradient: 'linear-gradient(145deg, #F59E0B, #FBBF24)',
    dot: '#F59E0B',
    bg: '#FEF3C7',
    mid: '#FDE68A',
    color: '#B45309',
    ring: 'rgba(245, 158, 11, 0.15)',
  },
  signed: {
    tier: 'signed',
    label: 'Signed',
    gradient: 'linear-gradient(145deg, #22C55E, #4ADE80)',
    dot: '#22C55E',
    bg: '#DCFCE7',
    mid: '#BBF7D0',
    color: '#15803D',
    ring: 'rgba(34, 197, 94, 0.20)',
  },
  closed: {
    tier: 'closed',
    label: 'Closed',
    gradient: 'linear-gradient(145deg, #94A3B8, #B0BEC5)',
    dot: '#94A3B8',
    bg: '#F1F5F9',
    mid: '#E2E8F0',
    color: '#64748B',
    ring: 'rgba(148, 163, 184, 0.12)',
  },
};

/** Map every raw backend status string → tier */
export function statusToTier(status: string): IntakyStatusTier {
  switch (status) {
    case 'in_progress':
      return 'new';
    case 'chase':
    case 'escalated':
    case 'sent_for_signature':
      return 'chase';
    case 'signed':
      return 'signed';
    case 'rejected':
    case 'archived':
    case 'lost':
      return 'closed';
    default:
      return 'new';
  }
}

/** Convenience: raw backend status → full tier config */
export function statusToTierConfig(status: string): StatusTierConfig {
  return INTAKY_TIERS[statusToTier(status)];
}

// ─── Legacy status map (used by table nested circles / status badge) ────────
// Kept as a thin adapter on top of the tier system so we don't break existing
// components during migration. Components should migrate to statusToTierConfig.

function legacyEntry(
  label: string,
  tier: StatusTierConfig,
): { label: string; color: string; bg: string; mid: string; dot: string } {
  return { label, color: tier.color, bg: tier.bg, mid: tier.mid, dot: tier.dot };
}

export const INTAKY_STATUS_MAP: Record<
  string,
  { label: string; color: string; bg: string; mid: string; dot: string }
> = {
  in_progress: legacyEntry('New', INTAKY_TIERS.new),
  sent_for_signature: legacyEntry('Retainer Sent', INTAKY_TIERS.chase),
  signed: legacyEntry('Signed', INTAKY_TIERS.signed),
  chase: legacyEntry('Following Up', INTAKY_TIERS.chase),
  escalated: legacyEntry('Escalated', INTAKY_TIERS.chase),
  archived: legacyEntry('Archived', INTAKY_TIERS.closed),
  rejected: legacyEntry('Declined', INTAKY_TIERS.closed),
  lost: legacyEntry('Lost', INTAKY_TIERS.closed),
};

// ─── Colours ────────────────────────────────────────────────────────────────

export const INTAKY_COLORS = {
  // Page & surface backgrounds
  pageBg: '#F4F5F7',
  cardBg: '#FFFFFF',
  tableBg: '#FFFFFF',
  tableHeaderBg: '#F9FAFB',
  tableRowHover: '#F8FAFC',
  tableRowNew: '#F0FDF6',

  // Brand
  brand: '#18181B',        // near-black logo mark
  brandAccent: '#4F46E5',  // indigo accent (used sparingly)

  // Status — derived from tier system
  statusGreen: INTAKY_TIERS.signed.color,
  statusGreenBg: INTAKY_TIERS.signed.bg,
  statusGreenDot: INTAKY_TIERS.signed.dot,
  statusYellow: INTAKY_TIERS.chase.color,
  statusYellowBg: INTAKY_TIERS.chase.bg,
  statusYellowDot: INTAKY_TIERS.chase.dot,
  statusRed: INTAKY_TIERS.chase.color,    // red is gone — escalated maps to chase/amber
  statusRedBg: INTAKY_TIERS.chase.bg,
  statusRedDot: INTAKY_TIERS.chase.dot,
  statusBlue: INTAKY_TIERS.new.color,
  statusBlueBg: INTAKY_TIERS.new.bg,
  statusBlueDot: INTAKY_TIERS.new.dot,

  // Text hierarchy
  textPrimary: '#0F172A',
  textSecondary: '#475569',
  textMuted: '#94A3B8',
  textLink: '#2563EB',

  // Borders — very subtle
  border: '#E2E8F0',
  borderStrong: '#CBD5E1',

  // Live indicator
  liveGreen: '#22C55E',

  // Map placeholder bg
  mapBg: '#D4E8F5',
} as const;

export const INTAKY_FONT = {
  family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  sizeXs: '11px',
  sizeSm: '12px',
  sizeMd: '13px',
  sizeLg: '15px',
  sizeXl: '18px',
  size2xl: '24px',
  size3xl: '30px',
  weightNormal: '400',
  weightMedium: '500',
  weightSemibold: '600',
  weightBold: '700',
} as const;

export const INTAKY_RADIUS = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  '2xl': '20px',
  full: '9999px',
} as const;

export const INTAKY_SHADOW = {
  card: '0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.04)',
  cardMd: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
  header: '0 1px 0 rgba(0,0,0,0.06)',
  glass: '0 8px 32px rgba(0,0,0,0.10)',
} as const;

/**
 * Visual centroids for each US state — adjusted so dots appear where
 * humans expect the "center" of the state to be on a map, not the
 * geographic centroid (which gets pulled by panhandles, coastlines, etc).
 */
export const INTAKY_STATE_COORDS: Record<string, [number, number]> = {
  AL: [32.81, -86.79], AK: [63.59, -154.49], AZ: [34.30, -111.70], AR: [34.80, -92.20],
  CA: [37.20, -119.50], CO: [39.00, -105.55], CT: [41.60, -72.76], DE: [39.00, -75.50],
  DC: [38.91, -77.04], FL: [28.75, -82.50], GA: [32.70, -83.50], HI: [20.50, -157.00],
  ID: [44.40, -114.60], IL: [40.00, -89.20], IN: [39.85, -86.26], IA: [42.01, -93.50],
  KS: [38.50, -98.35], KY: [37.80, -85.30], LA: [31.00, -92.00], ME: [45.37, -69.24],
  MD: [39.05, -76.80], MA: [42.30, -71.80], MI: [43.60, -84.70], MN: [46.20, -94.30],
  MS: [32.75, -89.68], MO: [38.46, -92.60], MT: [47.00, -109.60], NE: [41.50, -99.80],
  NV: [39.50, -117.00], NH: [43.68, -71.58], NJ: [40.20, -74.65], NM: [34.50, -106.00],
  NY: [42.90, -75.50], NC: [35.55, -79.80], ND: [47.45, -100.50], OH: [40.30, -82.80],
  OK: [35.50, -97.50], OR: [43.80, -120.55], PA: [41.00, -77.50], RI: [41.68, -71.51],
  SC: [33.86, -80.95], SD: [44.30, -100.20], TN: [35.85, -86.40], TX: [31.50, -99.50],
  UT: [39.30, -111.70], VT: [44.07, -72.67], VA: [37.50, -79.00], WA: [47.40, -120.50],
  WV: [38.60, -80.62], WI: [44.50, -89.80], WY: [43.00, -107.55],
};
